import React, { useState } from "react";
import { Button } from "antd";
import { Delete, Palette } from "lucide-react";
import useAxiosInstance from "../../../../../lib/useAxiosInstance";

/* ---------------------- Color Picker Modal ---------------------- */
const ColorPickerModal = ({ isOpen, item, onClose, onSave }) => {
  const [color, setColor] = useState(item?.color || "#000000");

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl border border-slate-100 p-6">

        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <Palette size={18} /> Select Color
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              Choose a color for:
              <span className="font-semibold text-slate-700"> {item?.text_value}</span>
              <br />
              <span className="text-[10px] text-slate-400">Applies to all locations</span>
            </p>
          </div>

          <button
            onClick={onClose}
            className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 text-sm"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="space-y-4">
          {/* Color Picker Input */}
          <div>
            <label className="text-xs font-medium text-slate-700 mb-1.5 block">
              Pick a Color
            </label>
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="w-20 h-12 cursor-pointer rounded-lg border border-slate-300"
            />
          </div>

          {/* HEX Input */}
          <div>
            <label className="text-xs font-medium text-slate-700 mb-1.5 block">
              HEX Code
            </label>
            <input
              type="text"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              placeholder="#000000"
              className="w-32 rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2 text-sm text-slate-800 focus:ring-2 focus:ring-sky-300 focus:border-sky-300"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-xl border border-slate-200 bg-white px-4 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
          >
            Cancel
          </button>

          <button
            onClick={() => onSave(color)}
            className="rounded-xl bg-gradient-to-r from-sky-500 to-indigo-500 px-4 py-1.5 text-xs font-semibold text-white shadow-md hover:shadow-lg"
          >
            Save Color
          </button>
        </div>

      </div>
    </div>
  );
};

/* ---------------------- MAIN LEGEND SECTION COMPONENT ---------------------- */

const LegendSection = ({
  configs,
  surveyId,
  surveyType,
  selectedConfigId,
  setSelectedConfigId,
  legendData,
  onDelete,
}) => {
  const axiosInstance = useAxiosInstance();
  const [blockColors, setBlockColors] = useState({});
  const [colorModalItem, setColorModalItem] = useState(null);

  /* ---------------------- Save Individual Color Locally ---------------------- */
  const saveLocalColor = (text_value, color) => {
    setBlockColors((prev) => ({
      ...prev,
      [text_value]: color,
    }));
  };

  /* ---------------------- SAVE ALL COLORS API ---------------------- */
  const handleSaveAll = async () => {
  try {
    const finalPayload = legendData.map((item) => ({
      survey_id: surveyId,
      text_value: item.text_value,
      location: item.location,
      color: blockColors[item.text_value] || item.color || "#000000",
    }));

    await axiosInstance.post(
      "/api/surveyAnalytics/surveyColor",
      finalPayload
    );

    window.location.reload();

  } catch (error) {
    console.error("Failed to save colors", error);
  }
};

  const displayLegendData = React.useMemo(() => {
  const map = {};

  legendData?.forEach(item => {
    const key = item.text_value;

    if (!map[key]) {
      map[key] = {
        text_value: item.text_value,
        color: item.color,
        frequency: item.frequency || 0,
      };
    } else {
      map[key].frequency += item.frequency || 0;
    }
  });
  return Object.values(map);
}, [legendData]);

  return (
    <div className="rounded-xl bg-white/80 backdrop-blur-lg border p-2 shadow-md">
      {/* Legend Config Selector */}
      <div className="flex flex-wrap gap-8 mb-3">
        {configs.map((cfg) => (
          <Button
            key={cfg._id}
            type={cfg._id === selectedConfigId ? "primary" : "default"}
            onClick={() => setSelectedConfigId(cfg._id)}
            className="bg-[#004877]"
          >
            {cfg.heading || "Color Legend"}
          </Button>
        ))}
        <Button danger onClick={onDelete} className="flex justify-center">
            <Delete size={20} />
        </Button>
      </div>

      {/* No Data */}
      {legendData?.length === 0 && (
        <div className="text-gray-500 text-sm">No legend data available.</div>
      )}

      {/* Legend List */}
      <div className="space-y-3">
        {displayLegendData?.map((item, index) => {
          const savedColor = blockColors[item.text_value] || item.color || "#000000";

          return (
            <div
              key={index}
              className="flex items-center justify-between p-2 border rounded-lg bg-white shadow-sm"
            >
              {/* Text Info */}
              <div>
                <div className="font-semibold">{item.text_value}</div>
                <div className="text-xs text-gray-500">
                 Frequency: {item.frequency}
                </div>
              </div>

              {/* Color Picker Trigger */}
              <div className="flex items-center gap-2">
                <Button
                  icon={<Palette size={18} />}
                  onClick={() =>
                    setColorModalItem({
                      text_value: item.text_value,
                      color: savedColor,
                    })
                  }
                />

                {/* Color Preview Bubble */}
                <div
                  className="w-6 h-6 rounded-full border"
                  style={{ backgroundColor: savedColor }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* ----- SAVE ALL BUTTON ----- */}
      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSaveAll}
          className="rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 px-5 py-2 text-xs font-semibold text-white shadow-md hover:shadow-lg"
        >
          Save All Colors
        </button>
      </div>

      {/* Modal */}
      <ColorPickerModal
        isOpen={!!colorModalItem}
        item={colorModalItem}
        onClose={() => setColorModalItem(null)}
        onSave={(color) => {
          saveLocalColor(colorModalItem.text_value, color);
          setColorModalItem(null);
        }}
      />
    </div>
  );
};

export default LegendSection;
